<template>
  <!-- Table Loading Overlay -->
  <AppLoader
    :isVisible="isLoading"
    variant="fullscreen"
    :message="tableLoadingMessage || 'Loading Student Directory...'"
    subtext="Please wait while we fetch and organize student records..."
    color="indigo"
    icon="cap"
  />

  <!-- Main Section -->
  <div
    class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] overflow-hidden relative shadow-xs"
  >
    <!-- Clean Table Header (Matching Reference Image) -->
    <div
      class="p-4 sm:p-5 border-b border-slate-100 dark:border-[#263449] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
    >
      <div>
        <h3
          class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight"
        >
          Student Directory
        </h3>
        <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Latest student profiles and academic records
        </p>
      </div>

      <div class="flex items-center gap-2 self-start sm:self-auto">
        <span
          v-if="selectedStudents.length > 0"
          class="text-xs font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/40 px-3 py-1 rounded-lg"
        >
          {{ selectedStudents.length }} selected
        </span>
        <span
          class="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-[#18243A] px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/50"
        >
          {{ students.length }} Records
        </span>
      </div>
    </div>

    <!-- Mobile View - Cards (< 768px) -->
    <div class="block md:hidden">
      <!-- Mobile Select All -->
      <div
        class="p-3 sm:p-3.5 border-b border-slate-100 dark:border-[#263449] bg-slate-50/70 dark:bg-[#18243A]/40 flex items-center justify-between"
      >
        <label class="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            :checked="isAllSelected"
            @change="$emit('toggle-select-all')"
            :disabled="isLoading || students.length === 0"
            class="w-4 h-4 text-indigo-600 bg-white dark:bg-[#111C30] border-slate-300 dark:border-slate-600 rounded focus:ring-indigo-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          />
          <span class="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
            Select All Students
          </span>
        </label>
        <span
          :class="[
            'text-[11px] font-semibold px-2 py-0.5 rounded-md transition-colors',
            selectedStudents.length > 0
              ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/40'
              : 'text-slate-400 dark:text-[#7E8DA5] bg-slate-100 dark:bg-[#18243A]',
          ]"
        >
          {{ selectedStudents.length > 0 ? `${selectedStudents.length} / ${students.length} selected` : `${students.length} total` }}
        </span>
      </div>

      <!-- Mobile Skeleton Loading -->
      <div
        v-if="isLoading"
        class="p-3 sm:p-4 space-y-3"
      >
        <div
          v-for="i in 4"
          :key="i"
          class="bg-white/95 dark:bg-[#111C30]/95 rounded-2xl border border-slate-200/80 dark:border-[#263449] overflow-hidden shadow-xs"
        >
          <div class="h-1 w-full bg-slate-200 dark:bg-[#1E293B] skeleton-shimmer"></div>
          <div class="p-3.5 space-y-3">
            <div class="flex items-center justify-between">
              <div class="h-5 skeleton-shimmer rounded-md w-24"></div>
              <div class="h-5 skeleton-shimmer rounded-full w-16"></div>
            </div>
            <div class="flex items-center gap-3">
              <div class="h-12 w-12 skeleton-shimmer rounded-xl flex-shrink-0"></div>
              <div class="space-y-2 flex-1 min-w-0">
                <div class="h-4 skeleton-shimmer rounded-md w-32"></div>
                <div class="h-3 skeleton-shimmer rounded-md w-24"></div>
              </div>
            </div>
            <div class="h-8 skeleton-shimmer rounded-xl w-full"></div>
          </div>
        </div>
      </div>

      <!-- Mobile Cards (Simple, Attractive & Premium Design) -->
      <div v-else class="p-3 space-y-3.5 bg-slate-50/40 dark:bg-[#0B1120]/30">
        <div
          v-for="student in students"
          :key="student._id || student.id"
          class="group transition-all duration-200 touch-manipulation relative overflow-hidden bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl shadow-xs hover:shadow-md flex flex-col w-full min-w-0"
          :class="{
            'ring-2 ring-indigo-500 shadow-md bg-indigo-50/15 dark:bg-indigo-950/20 border-indigo-400 dark:border-indigo-500': isStudentSelected(student._id),
          }"
        >
          <!-- Card Header & Identity Row -->
          <div
            class="p-3.5 space-y-3 cursor-pointer"
            @click="$emit('view-student', student)"
          >
            <!-- Top Strip: Checkbox + Roll Number & Status -->
            <div
              class="flex items-center justify-between gap-2 pb-2.5 border-b border-slate-100 dark:border-[#263449]/70"
              @click.stop
            >
              <!-- Checkbox with Roll & ID Pill -->
              <div class="flex items-center gap-2 min-w-0">
                <label class="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    :checked="isStudentSelected(student._id)"
                    @change="$emit('toggle-student-selection', student._id)"
                    :disabled="isLoading"
                    class="w-4 h-4 text-indigo-600 bg-white dark:bg-[#111C30] border-slate-300 dark:border-slate-600 rounded-md focus:ring-indigo-500 focus:ring-2 cursor-pointer transition-colors"
                  />
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-[#18243A] text-[11px] font-mono font-bold text-slate-700 dark:text-slate-200 border border-slate-200/70 dark:border-[#263449]"
                  >
                    <Hash class="w-3 h-3 text-slate-400" />
                    Roll #{{ student.rollNumber || 'N/A' }}
                  </span>
                </label>
                <span
                  v-if="student.attendanceID"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-50 dark:bg-[#18243A]/60 text-[10px] font-mono font-medium text-slate-500 dark:text-[#A6B4C8] border border-slate-200/50 dark:border-[#263449]/50 truncate max-w-[100px]"
                >
                  <IdCard class="w-3 h-3 text-slate-400 shrink-0" />
                  ID: {{ student.attendanceID }}
                </span>
              </div>

              <!-- Animated Status Pill -->
              <span
                :class="[
                  'inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] sm:text-[11px] font-bold rounded-full uppercase tracking-wider shrink-0',
                  student.status === 'active'
                    ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-600/20'
                    : student.status === 'leaved'
                    ? 'bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 ring-1 ring-rose-600/20'
                    : student.status === 'passout'
                    ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-600/20'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 ring-1 ring-slate-400/20',
                ]"
              >
                <span
                  :class="[
                    'w-1.5 h-1.5 rounded-full',
                    student.status === 'active'
                      ? 'bg-emerald-500 animate-pulse'
                      : student.status === 'leaved'
                      ? 'bg-rose-500'
                      : student.status === 'passout'
                      ? 'bg-indigo-500 animate-pulse'
                      : 'bg-slate-400',
                  ]"
                ></span>
                {{ student.status === "leaved" ? "Inactive" : student.status }}
              </span>
            </div>

            <!-- Student Profile: Avatar + Name + Class -->
            <div class="flex items-center gap-3">
              <!-- Avatar: STRICT bounded dimensions to prevent any image blowout -->
              <div class="relative shrink-0">
                <div
                  class="w-12 h-12 min-w-[48px] max-w-[48px] min-h-[48px] max-h-[48px] rounded-2xl flex items-center justify-center border border-slate-200/90 dark:border-[#263449] bg-slate-100 dark:bg-[#18243A] overflow-hidden shadow-xs ring-2 ring-slate-100 dark:ring-[#263449]"
                >
                  <img
                    v-if="student.photo"
                    :src="student.photo"
                    alt="Student Photo"
                    class="w-full h-full object-cover block"
                  />
                  <div
                    v-else
                    :style="{ backgroundColor: getAvatarColor(student.name) }"
                    class="w-full h-full flex items-center justify-center text-white font-extrabold text-sm uppercase"
                  >
                    {{ getInitials(student.name) }}
                  </div>
                </div>
              </div>

              <!-- Name & Class Badge -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-1.5">
                  <h3 class="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    {{ student.name }}
                  </h3>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-bold bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/40 shrink-0"
                  >
                    <GraduationCap class="w-3.5 h-3.5 text-indigo-500" />
                    {{
                      typeof student.class === "object"
                        ? `${student.class?.name || ""}${
                            student.class?.section
                              ? " - " + student.class.section.replace(/section\s*/i, "")
                              : ""
                          }`
                        : student.class || "N/A"
                    }}
                  </span>
                </div>

                <!-- Email Link -->
                <div class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-[#A6B4C8] mt-1 truncate">
                  <Mail class="w-3.5 h-3.5 text-slate-400 dark:text-[#7E8DA5] shrink-0" />
                  <a
                    v-if="student.email"
                    :href="'mailto:' + student.email"
                    @click.stop
                    class="truncate hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline"
                  >
                    {{ student.email }}
                  </a>
                  <span v-else class="text-slate-400 dark:text-[#7E8DA5]">No email provided</span>
                </div>
              </div>
            </div>

            <!-- Detailed Info Grid -->
            <div
              class="grid grid-cols-2 gap-2 p-2.5 rounded-xl bg-slate-50/80 dark:bg-[#18243A]/60 border border-slate-100 dark:border-[#263449]/60 text-xs"
            >
              <!-- Primary Phone -->
              <div class="flex items-center gap-1.5 min-w-0 text-slate-600 dark:text-[#A6B4C8]">
                <Phone class="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <a
                  v-if="student.phone"
                  :href="'tel:' + student.phone"
                  @click.stop
                  class="font-mono truncate hover:text-indigo-600 font-medium"
                >
                  {{ student.phone }}
                </a>
                <span v-else class="truncate text-slate-400">No phone</span>
              </div>

              <!-- Guardian Phone or Secondary Phone -->
              <div class="flex items-center gap-1.5 min-w-0 text-slate-600 dark:text-[#A6B4C8]">
                <Users class="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <a
                  v-if="student.secondaryPhone"
                  :href="'tel:' + student.secondaryPhone"
                  @click.stop
                  class="font-mono truncate hover:text-indigo-600 font-medium"
                  :title="'Guardian: ' + student.secondaryPhone"
                >
                  {{ student.secondaryPhone }}
                </a>
                <span v-else class="truncate text-slate-400">Guardian: —</span>
              </div>

              <!-- Location / City -->
              <div class="flex items-center gap-1.5 min-w-0 text-slate-600 dark:text-[#A6B4C8]">
                <MapPin class="w-3.5 h-3.5 text-rose-500 shrink-0" />
                <span class="truncate" :title="[student.city, student.state].filter(Boolean).join(', ') || student.address">
                  {{ [student.city, student.state].filter(Boolean).join(", ") || student.address || "No address" }}
                </span>
              </div>

              <!-- Admission Date -->
              <div class="flex items-center gap-1.5 min-w-0 text-slate-600 dark:text-[#A6B4C8]">
                <Calendar class="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                <span class="truncate">
                  Admitted: {{ formatDate(student.enrollmentDate || student.createdAt) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Actions Bar -->
          <div
            class="px-3 py-2.5 bg-slate-50/70 dark:bg-[#18243A]/40 border-t border-slate-100 dark:border-[#263449]/70 flex items-center gap-2"
          >
            <!-- View Profile Button (Primary) -->
            <button
              @click.stop="$emit('view-student', student)"
              :disabled="isLoading"
              type="button"
              class="flex-1 py-2 px-3 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white transition-all shadow-xs shadow-indigo-600/20 flex items-center justify-center gap-1.5 active:scale-[0.98] cursor-pointer min-h-[38px]"
            >
              <Eye class="w-3.5 h-3.5" />
              <span>View Profile</span>
            </button>

            <!-- Quick Call Button (if phone exists) -->
            <a
              v-if="student.phone"
              :href="'tel:' + student.phone"
              class="p-2 rounded-xl text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200/80 dark:border-emerald-800/50 transition-all shadow-xs flex items-center justify-center active:scale-[0.98] min-h-[38px] min-w-[38px]"
              title="Call Student"
            >
              <PhoneCall class="w-3.5 h-3.5" />
            </a>

            <!-- Edit Button -->
            <button
              @click.stop="$emit('edit-student', student)"
              :disabled="isLoading"
              type="button"
              class="p-2 rounded-xl text-xs font-semibold bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#1f2d47] text-slate-700 dark:text-[#F1F5F9] border border-slate-200 dark:border-[#263449] transition-all shadow-xs flex items-center justify-center active:scale-[0.98] cursor-pointer min-h-[38px] min-w-[38px]"
              title="Edit Student"
            >
              <Pencil class="w-3.5 h-3.5" />
            </button>

            <!-- Delete Button -->
            <button
              @click.stop="$emit('delete-student', student._id, student.name)"
              :disabled="isLoading || isDeleting"
              type="button"
              class="p-2 rounded-xl text-xs font-semibold bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200/80 dark:border-rose-900/40 transition-all shadow-xs flex items-center justify-center active:scale-[0.98] cursor-pointer disabled:opacity-50 min-h-[38px] min-w-[38px]"
              title="Delete Student"
            >
              <Trash2 class="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <!-- Mobile Empty State -->
        <div
          v-if="!isLoading && students.length === 0"
          class="text-center py-14 px-4"
        >
          <div
            class="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/40 rounded-2xl flex items-center justify-center mx-auto mb-3 text-indigo-500"
          >
            <ClipboardList class="w-6 h-6" />
          </div>
          <h3 class="text-sm font-bold text-slate-900 dark:text-white mb-0.5">
            No students found
          </h3>
          <p class="text-xs text-slate-400 dark:text-slate-500">
            Try adjusting your search criteria or add new students.
          </p>
        </div>
      </div>
    </div>

    <!-- Desktop View - Table (≥ 768px) -->
    <div class="hidden md:block overflow-x-auto">
      <!-- Desktop Skeleton Loading -->
      <div
        v-if="isLoading"
        class="p-4 lg:p-6"
      >
        <div class="space-y-3">
          <div
            v-for="i in 6"
            :key="i"
            class="flex items-center space-x-4 py-3.5 border-b border-slate-100 dark:border-[#18243A] last:border-b-0"
          >
            <div class="w-4 h-4 skeleton-shimmer rounded"></div>
            <div class="h-4 skeleton-shimmer rounded-md w-16"></div>
            <div class="flex items-center space-x-3 flex-1">
              <div class="w-9 h-9 skeleton-shimmer rounded-xl flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-4 skeleton-shimmer rounded-md w-32"></div>
                <div class="h-3 skeleton-shimmer rounded-md w-24"></div>
              </div>
            </div>
            <div class="h-4 skeleton-shimmer rounded-md w-24"></div>
            <div class="h-4 skeleton-shimmer rounded-md w-20"></div>
            <div class="h-6 skeleton-shimmer rounded-full w-16"></div>
            <div class="h-4 skeleton-shimmer rounded-md w-20"></div>
            <div class="h-8 skeleton-shimmer rounded-xl w-20"></div>
          </div>
        </div>
      </div>

      <!-- Actual Desktop Table (Matching Reference Style) -->
      <table
        v-else
        class="w-full text-left border-collapse"
      >
        <thead class="bg-slate-50/60 dark:bg-[#18243A]/40 border-b border-slate-100 dark:border-[#263449]">
          <tr>
            <th class="w-12 px-4 py-3.5 text-center">
              <input
                type="checkbox"
                :checked="isAllSelected"
                @change="$emit('toggle-select-all')"
                :disabled="isLoading || students.length === 0"
                class="w-4 h-4 text-indigo-600 bg-white dark:bg-[#111C30] border-slate-300 dark:border-slate-600 rounded focus:ring-indigo-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
            </th>
            <th
              class="px-4 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              ROLL
            </th>
            <th
              class="px-4 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              STUDENT
            </th>
            <th
              class="px-4 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              CONTACT
            </th>
            <th
              class="px-4 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              CLASS
            </th>
            <th
              class="px-4 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              STATUS
            </th>
            <th
              class="px-4 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              ENROLLMENT
            </th>
            <th
              class="pl-4 pr-14 sm:pr-16 lg:pr-20 py-3.5 text-right text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              ACTIONS
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-[#263449]">
          <tr
            v-for="student in students"
            :key="student._id || student.id"
            class="hover:bg-slate-50/70 dark:hover:bg-[#18243A]/40 transition-colors duration-150"
            :class="{
              'bg-indigo-50/20 dark:bg-indigo-950/20': isStudentSelected(student._id),
            }"
          >
            <!-- Checkbox -->
            <td class="w-12 px-4 py-3.5 text-center whitespace-nowrap">
              <input
                type="checkbox"
                :checked="isStudentSelected(student._id)"
                @change="$emit('toggle-student-selection', student._id)"
                :disabled="isLoading"
                class="w-4 h-4 text-indigo-600 bg-white dark:bg-[#111C30] border-slate-300 dark:border-slate-600 rounded focus:ring-indigo-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
            </td>

            <!-- Roll No -->
            <td class="px-4 py-3.5 whitespace-nowrap">
              <span class="text-xs font-bold text-slate-800 dark:text-slate-200 font-mono">
                #{{ student.rollNumber || 'N/A' }}
              </span>
            </td>

            <!-- Student Profile -->
            <td
              class="px-4 py-3.5 whitespace-nowrap cursor-pointer"
              @click="$emit('view-student', student)"
            >
              <div class="flex items-center gap-3">
                <div class="relative flex-shrink-0">
                  <div
                    class="h-9 w-9 min-w-[36px] max-w-[36px] min-h-[36px] max-h-[36px] rounded-xl flex items-center justify-center border border-slate-200 dark:border-[#263449] bg-slate-100 dark:bg-[#18243A] overflow-hidden"
                  >
                    <img
                      v-if="student.photo"
                      :src="student.photo"
                      alt="Student Photo"
                      class="h-full w-full object-cover block"
                    />
                    <span
                      v-else
                      class="text-xs font-bold text-slate-700 dark:text-slate-300"
                    >
                      {{ getInitials(student.name) }}
                    </span>
                  </div>
                </div>
                <div>
                  <div class="text-sm font-bold text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                    {{ student.name }}
                  </div>
                  <div class="text-xs text-slate-400 dark:text-slate-400 mt-0.5">
                    {{ student.email }}
                  </div>
                </div>
              </div>
            </td>

            <!-- Contact -->
            <td class="px-4 py-3.5 whitespace-nowrap">
              <div class="text-xs font-medium text-slate-700 dark:text-slate-200">
                {{ student.phone || "N/A" }}
              </div>
              <div class="text-[11px] text-slate-400 truncate max-w-36" :title="student.address || student.city">
                {{ student.city || student.address || "N/A" }}
              </div>
            </td>

            <!-- Class & Section -->
            <td class="px-4 py-3.5 whitespace-nowrap">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/50"
              >
                {{
                  typeof student.class === "object"
                    ? `${student.class?.name || ""}${
                        student.class?.section
                          ? " - " + student.class.section.replace(/section\s*/i, "")
                          : ""
                      }`
                    : student.class || "N/A"
                }}
              </span>
            </td>

            <!-- Status -->
            <td class="px-4 py-3.5 whitespace-nowrap">
              <span
                :class="getStatusClass(student.status)"
                class="inline-flex items-center px-2.5 py-0.5 text-[11px] font-semibold rounded-md border capitalize"
              >
                {{ student.status === "leaved" ? "inactive" : student.status }}
              </span>
            </td>

            <!-- Enrollment Date -->
            <td class="px-4 py-3.5 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
              {{ formatDate(student.enrollmentDate) }}
            </td>

            <!-- Actions -->
            <td class="pl-4 pr-10 sm:pr-12 lg:pr-16 py-3.5 whitespace-nowrap text-right">
              <div class="inline-flex items-center gap-2">
                <button
                  @click="$emit('view-student', student)"
                  :disabled="isLoading"
                  class="p-1.5 text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-lg transition-colors cursor-pointer"
                  title="View Profile"
                >
                  <Eye class="w-4 h-4" />
                </button>
                <button
                  @click="$emit('edit-student', student)"
                  :disabled="isLoading"
                  class="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-lg transition-colors cursor-pointer"
                  title="Edit Student"
                >
                  <Pencil class="w-4 h-4" />
                </button>
                <button
                  @click="$emit('delete-student', student._id, student.name)"
                  :disabled="isLoading || isDeleting"
                  class="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-[#18243A] rounded-lg transition-colors"
                  title="Delete"
                >
                  <AppLoader
                    v-if="isDeleting && deletingStudentId === student._id"
                    variant="button"
                    class="w-4 h-4 text-rose-600"
                  />
                  <Trash2
                    v-else
                    class="w-4 h-4"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Desktop Empty State (Matching Reference Image Exactly) -->
      <div
        v-if="!isLoading && students.length === 0"
        class="text-center py-16 px-4"
      >
        <div
          class="w-14 h-14 bg-blue-50 dark:bg-blue-950/40 rounded-2xl flex items-center justify-center mx-auto mb-3 text-blue-500"
        >
          <ClipboardList class="w-7 h-7" />
        </div>
        <h3 class="text-base font-bold text-slate-900 dark:text-white mb-1">
          No student records found
        </h3>
        <p class="text-xs text-slate-400 dark:text-slate-500 max-w-sm mx-auto">
          Try adjusting your filters or check back later.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    Mail,
    Phone,
    PhoneCall,
    Eye,
    ClipboardList,
    Trash2,
    Pencil,
    Calendar,
    GraduationCap,
    Hash,
    IdCard,
    MapPin,
    Users,
  } from "lucide-vue-next";

  const props = defineProps({
    students: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDeleting: {
      type: Boolean,
      default: false,
    },
    deletingStudentId: {
      type: String,
      default: null,
    },
    selectedStudents: {
      type: Array,
      default: () => [],
    },
    isAllSelected: {
      type: Boolean,
      default: false,
    },
    tableLoadingMessage: {
      type: String,
      default: "Loading...",
    },
  });

  defineEmits([
    "toggle-select-all",
    "toggle-student-selection",
    "view-student",
    "edit-student",
    "delete-student",
  ]);

  // Utility functions
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "N/A";

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "NA";

  const getAvatarColor = (name) => {
    const colors = [
      "#3B82F6",
      "#6366F1",
      "#8B5CF6",
      "#EC4899",
      "#10B981",
      "#F59E0B",
      "#06B6D4",
    ];
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "active":
        return "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-500/30";
      case "leaved":
        return "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200/60 dark:border-rose-500/30";
      case "passout":
        return "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200/60 dark:border-blue-500/30";
      default:
        return "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700";
    }
  };

  const isStudentSelected = (studentId) => {
    return props.selectedStudents?.includes(studentId) || false;
  };
</script>

<style scoped>
  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
